import api from './api';
import type { AuthResponse } from '../../../shared/types';
import { StorageManager } from '../utils/storageManager';
import { isTwoFactorChallenge, storePendingTwoFactorToken } from './twoFactorService';

interface LoginData {
  email: string;
  password: string;
}

interface AgencyRegisterData {
  name: string;
  email: string;
  website: string;
  phone: string;
  address: string;
  state: string;
  adminName: string;
  adminEmail: string;
  adminPhone: string;
}

export const login = async (data: LoginData, isAdmin: boolean = false): Promise<AuthResponse> => {
  try {
    const endpoint = isAdmin ? '/auth/admin/login' : '/auth/login';
    console.log('Making login request to:', endpoint);
    console.log('Login data:', { email: data.email, isAdmin });
    const response = await api.post<AuthResponse>(endpoint, data);
    console.log('Login service response:', response);
    console.log('Login service response data:', response.data);

    if (isTwoFactorChallenge(response.data)) {
      if (response.data.pendingToken) {
        storePendingTwoFactorToken(response.data.pendingToken);
      }
      return response.data;
    }

    if (!response.data || !response.data.user || !response.data.token) {
      console.error('Invalid response format:', response.data);
      throw new Error('Invalid response format');
    }
    const storageSuccess = StorageManager.setToken(response.data.token);
    if (!storageSuccess) {
      throw new Error('Failed to store authentication token');
    }
    return response.data;
  } catch (error: any) {
    console.error('Login service error:', error);
    throw error;
  }
};

// Individual plan login - dedicated URL, matches by exact email only
export const individualLogin = async (data: LoginData): Promise<AuthResponse> => {
  try {
    const response = await api.post<AuthResponse>('/auth/individual/login', data);
    if (isTwoFactorChallenge(response.data)) {
      if (response.data.pendingToken) storePendingTwoFactorToken(response.data.pendingToken);
      return response.data;
    }
    if (!response.data || !response.data.user || !response.data.token) {
      throw new Error('Invalid response format');
    }
    const storageSuccess = StorageManager.setToken(response.data.token);
    if (!storageSuccess) {
      throw new Error('Failed to store authentication token');
    }
    return response.data;
  } catch (error: any) {
    console.error('Individual login service error:', error);
    throw error;
  }
};

// Agency-specific login using agency slug
export const agencyLogin = async (agencySlug: string, data: LoginData): Promise<AuthResponse> => {
  try {
    const endpoint = `/auth/agency/${agencySlug}/login`;
    const response = await api.post<AuthResponse>(endpoint, data);

    if (isTwoFactorChallenge(response.data)) {
      if (response.data.pendingToken) storePendingTwoFactorToken(response.data.pendingToken);
      return response.data;
    }

    if (!response.data || !response.data.user || !response.data.token) {
      throw new Error('Invalid response format');
    }
    const storageSuccess = StorageManager.setToken(response.data.token);
    if (!storageSuccess) {
      throw new Error('Failed to store authentication token');
    }
    return response.data;
  } catch (error: any) {
    console.error('Agency login service error:', error);
    throw error;
  }
};

export const registerAgency = async (data: AgencyRegisterData): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/agencies/register', data);
  return response.data;
};

export const getProfile = async (isAdmin: boolean = false): Promise<AuthResponse> => {
  try {
    const endpoint = isAdmin ? '/auth/admin/profile' : '/auth/profile';
    console.log('Getting profile from endpoint:', endpoint);
    
    const token = StorageManager.getToken();
    if (!token) {
      console.error('No token found in localStorage');
      console.log('Storage test result:', StorageManager.testStorage());
      throw new Error('No authentication token found');
    }
    
    console.log('Making profile request with token');
    const response = await api.get<AuthResponse>(endpoint);
    console.log('Profile response:', response.data);
    
    if (!response.data || !response.data.user) {
      console.error('Invalid profile response:', response.data);
      throw new Error('Invalid profile response');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error getting profile:', error);
    throw error;
  }
};

export const logout = () => {
  StorageManager.removeToken();
};

export const verifyEmail = async (token: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/verify-email', { token });
  return response.data;
};

export const setPassword = async (token: string, password: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/auth/set-password', { token, password });
  return response.data;
};

export const deleteAccount = async (): Promise<void> => {
  await api.delete('/auth/account');
}; 